import type { Editor, Extension, Range, TiptapEditorProps } from 'OpalChat/TiptapEditorTypes';
import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { Box, Skeleton } from '@optiaxiom/react';
import { useDecision } from '@optimizely/react-sdk';

// Fallback component when TiptapEditor fails to load
const FallbackEditor: React.FC<TiptapEditorProps> = () => (
  <Box p="12" border="1" borderColor="border.default" rounded="md">
    TiptapEditor failed to load. Please refresh the page.
  </Box>
);

// Lazy load TiptapEditor from the OpalChat remote
const TiptapEditor = React.lazy<React.ComponentType<TiptapEditorProps>>(() =>
  import('OpalChat/TiptapEditor')
    .then(module => ({
      default: module.TiptapEditor,
    }))
    .catch(error => {
      console.error('Failed to load OpalChat TiptapEditor component:', error);
      return {
        default: FallbackEditor,
      };
    })
);

type Selectable = {
  name: string;
  description?: string | null;
};

const VARIABLE_PATTERN = '\\[\\[(.*?)\\]\\]';

type TiptapEditorWrapperProps = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  selectables?: Selectable[];
  usedIn: 'agent-form' | 'instruction-form';
  disabled?: boolean;
  suggestionExtensions?: Extension[];
};

const TiptapEditorWrapper = ({
  value = '',
  onChange,
  placeholder = '',
  className,
  selectables = [],
  usedIn,
  disabled = false,
  suggestionExtensions,
}: TiptapEditorWrapperProps) => {
  const selectablesRef = useRef(selectables);

  useEffect(() => {
    selectablesRef.current = selectables;
  }, [selectables]);

  const commands = useMemo(() => {
    return [
      {
        char: '/',
        items: (query: string) => {
          return selectablesRef.current
            .filter(selectable => selectable.name.toLowerCase().includes(query.toLowerCase()))
            .map(selectable => ({
              id: selectable.name,
              label: selectable.name,
              description: selectable.description || '',
              command: ({ editor, range }: { editor: Editor; range: Range }) => {
                editor
                  .chain()
                  .focus()
                  .deleteRange(range)
                  .insertContent(`[[${selectable.name}]]`)
                  .run();
              },
            }));
        },
      },
    ];
  }, []);

  const [newSuggestionListDecision] = useDecision('prompt-template-new-suggestion-list');

  return (
    <Suspense fallback={<Skeleton h="56" />}>
      <Box
        w="full"
        border="1"
        borderColor="border.default"
        rounded="md"
        bg="bg.default"
        p="10"
        overflow="auto"
        className={className}
      >
        <TiptapEditor
          content={value}
          onChange={onChange}
          commands={
            usedIn === 'agent-form' && !newSuggestionListDecision.enabled ? commands : undefined
          } // for now
          isReadOnly={disabled}
          placeholder={placeholder}
          patternsToHighlight={selectables.length > 0 ? [VARIABLE_PATTERN] : undefined}
          suggestions={newSuggestionListDecision.enabled ? suggestionExtensions : undefined} // for now
          enableDragging
        />
      </Box>
    </Suspense>
  );
};

export { TiptapEditorWrapper };
